import { Moment } from "moment";
import { WAPIStore, AuthStore } from "./wa-store-types";

export interface MediaFileDescirptor {
  _blob: Blob;
}
export interface MediaDescriptor {
  file: Blob | File | MediaFileDescirptor;
  filename: string;
  mimetype: string;
}
export interface ComposeContents {
  text?: string;
}

/**
 * WAWeb's wid (WhatsApp ID) shape. Runtime-verified 2026-05-26 against
 * both DM (@c.us) and group (@g.us) chats: `{ server, user, _serialized }`
 * are ALWAYS present together. Storage backs them with `__x_id`; the
 * getters above are observable accessors.
 *
 * `isGroup` is kept as an optional method because some legacy WAWeb wid
 * instances expose it — but the canonical disambiguator is `server === 'g.us'`.
 */
export interface WhatsappId {
  server: 'c.us' | 'g.us' | 'lid' | 'newsletter' | 'broadcast' | (string & {});
  user: string;
  _serialized: string;
  isGroup?: () => boolean;
}

/**
 * Parse a serialized wid string (`user@server`) into a structural WhatsappId.
 * Returns `undefined` if the string is missing `@` or either half is empty —
 * callers that get `undefined` should treat it as an unresolved id.
 *
 * This is NOT a substitute for `Store.WidFactory.createWid` when the WAWeb
 * store is available (that returns a model with prototype methods). Use this
 * only where the codebase needs a plain `WhatsappId` shape for typed payloads
 * (action dispatches, RTK reducers, etc.).
 */
export function buildWhatsappIdFromSerialized(serialized: string): WhatsappId | undefined {
  if (!serialized || typeof serialized !== 'string') return undefined;
  const trimmed = serialized.includes('@') ? serialized : `${serialized}@c.us`;
  const atIndex = trimmed.indexOf('@');
  if (atIndex <= 0 || atIndex === trimmed.length - 1) return undefined;
  const user = trimmed.slice(0, atIndex);
  const server = trimmed.slice(atIndex + 1) as WhatsappId['server'];
  return { user, server, _serialized: trimmed };
}

export interface WhatsappMessageId {
  id?: string;
  fromMe?: boolean;
  remote?: WhatsappId;
  participant?: WhatsappId;
  // `_serialized` REMOVED: WhatsApp Web dropped it from the raw MsgKey model
  // (2.3000.10431xxxxx, 2026-07). Reading it off a MsgKey silently returns
  // undefined → route through getSerialized(key). Wid (WhatsappId) KEEPS
  // `_serialized` — only the message key lost it.
}

export interface ProfilePicThumb {
  imgFull?: string;
  img?: string;
  id?: WhatsappId;
  eurl?: string;
  profilePicUrl?: string;
}
/**
 * WAWeb contact runtime shape (verified 2026-05-26 via getContact()).
 * The 39 fields below mirror the live `WWebJSBt.getContact()` return:
 * accessor-exposed fields (not the `__x_*` internal storage).
 *
 * WAWeb spells it `pushname` (lowercase n). The legacy `pushName`
 * (capital N) is kept as a compatibility alias because some older
 * callsites read it. Prefer `pushname` for new code.
 */
export interface WhatsappContact {
  _id?: string;
  id?: WhatsappId;
  name: string;
  formattedTitle?: string;
  displayName?: string;
  shortName?: string;
  pushname?: string;
  /** Legacy alias for `pushname`. Older WAWeb builds capitalized the N. */
  pushName?: string;
  notifyName?: string;
  formattedShortName?: string;
  imageUrl?: string;
  profilePicThumb?: ProfilePicThumb;
  type?: string;
  isWAContact?: boolean;
  isMyContact?: boolean;
  isBusiness?: boolean;
  isEnterprise?: boolean;
  isSmb?: boolean;
  isBlocked?: boolean;
  isMe?: boolean;
  isUser?: boolean;
  isGroup?: boolean;
  isUsernameContact?: boolean;
  isContactSyncCompleted?: boolean;
  verifiedLevel?: number;
  privacyMode?: number;
  statusMute?: boolean;
  parentStatusMute?: boolean;
  labels?: string[];
  userid?: string;
  username?: string;
  usernameKey?: string;
  usernameCountryCode?: string;
  getProfilePicThumb?: () => ProfilePicThumb;
  __x_verifiedName?: string; // as imported from WA
  verifiedName?: string; // for more convenient use in the app
  // Enhanced fields from frontendContactGetter
  formattedPhone?: string;
  phoneType?: number;
  formattedName?: string;
  nameType?: number;
  /**
   * Phone-form wid resolved from a @lid contact. Present only after the
   * WAWeb store has resolved the lid→phone mapping; absent (or pointing at
   * the lid itself) until then. Verified runtime field, present on @lid
   * contacts post-resolution.
   */
  phoneNumber?: WhatsappId;
}

export interface WhatsappChatMessage {
  id: WhatsappMessageId;
  body?: string;
  to?: WhatsappId;
  from?: WhatsappId;
  author?: WhatsappId;
  chat?: Chat;
  chatId?: WhatsappId;
  t?: number;
  isSentByMe?: boolean;
  mimetype?: string;
  filename?: string;
  ack?: number;
  isUnreadType?: boolean;
  mediaObject?: {
    filehash?: string;
  };
  downloadMedia?: (options: {
    downloadEvenIfExpensive: boolean;
    isUserInitiated: boolean;
    rmrReason: number;
  }) => StackPromise<any>;
  caption?: string;
  quotedMsg?: {
    body?: string;
    type?: string;
  };
  quotedParticipant?: WhatsappId;
  blob?: Blob;
  fromMe?: boolean;
}

export interface StackPromise<T> extends Promise<T> {
  value: () => T | undefined;
}

/**
 * Mobx-style WAWeb collection wrapper. The actual element array is
 * `_models` — DO NOT iterate the collection itself. Verified 2026-05-26
 * against `chat.groupMetadata.participants` which is a collection-with-_models
 * wrapper (NOT a bare array).
 *
 * `get`, `getModelsArray`, `length` are the read accessors WAWeb exposes.
 * `msgLoadState` and `loadEarlierPromise` are present on the messages
 * sub-collection (`chat.msgs`) for history-load coordination.
 */
export interface WhatsappCollection<T> {
  _models: Array<T>;
  _events?: any;
  length?: number;
  get?: (id: string | WhatsappId | WhatsappContact | WhatsappMessageId) => T | undefined;
  getModelsArray?: () => T[];
  find: (
    id: string | WhatsappId | WhatsappContact | WhatsappMessageId
  ) => StackPromise<T>;
  listenTo: (
    collection: WhatsappCollection<T>,
    action: string,
    callback: (...args: any[]) => void
  ) => void;
  on: (event: string, callback: (...args: any[]) => void) => void;
  off: (event: string, callback: (...args: any[]) => void) => void;
  gadd: (id: any) => T;
  add: any;
  // Present on the per-chat messages sub-collection — drives history load.
  msgLoadState?: {
    noEarlierMsgs?: boolean;
    isLoadingEarlierMsgs?: boolean;
    isLoadingAroundMsgs?: boolean;
  };
  /**
   * Promise tracking the in-flight loadEarlier call. `null` means none.
   * Resolves to the array of newly-fetched message models — the same shape
   * `ConversationMsgs.loadEarlierMsgs` returns (paged history page).
   */
  loadEarlierPromise?: Promise<T[]> | null;
}

export interface SendMessageOptions {
  ctwaContext: any;
  linkPreview: any;
  mentionedJidList: any[];
  quotedMsg?: WhatsappChatMessage;
  quotedMsgAdminGroupJid: any;
}

/**
 * WAWeb group-participant runtime shape (verified 2026-05-26).
 * Stored on the model as `__x_id`, `__x_isAdmin`, `__x_isSuperAdmin`,
 * `__x_contact`; exposed via observable getters as the un-prefixed names.
 *
 * `contact` is populated lazily — present once the contact collection
 * has resolved this wid, absent otherwise. Always check.
 */
export interface Participant {
  id: WhatsappId;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  contact?: WhatsappContact;
}

/**
 * WAWeb group-metadata runtime shape (verified 2026-05-26).
 * Only fields the codebase actually reads are declared — the model has
 * 50+ accessors but we don't need to enumerate every one. Add more as
 * callsites are tightened; leave deliberately incomplete rather than
 * lying with `any`.
 *
 * `participants` (and pendingParticipants / pastParticipants) is a Mobx
 * collection wrapper. Access the array via `.participants._models`.
 * Direct array-iteration on the wrapper crashes (it's not an array).
 */
export interface GroupMetaData {
  id: WhatsappId;
  subject: string;
  creation: number;
  owner: WhatsappId;
  participants: WhatsappCollection<Participant>;
  pendingParticipants: WhatsappCollection<Participant>;
  pastParticipants: WhatsappCollection<Participant>;
  /** `true` = only admins can edit group info / picture / subject. */
  restrict: boolean;
  /** `true` = only admins can send messages (announcement-only group). */
  announce: boolean;
  ephemeralDuration: number;
  membershipApprovalMode: boolean;
  size: number;
  isParentGroup: boolean;
  isParentGroupClosed: boolean;
  generalSubgroup: boolean;
  lastActivityTimestamp: number;
  isLidAddressingMode: boolean;
  memberAddMode?: string;
  displayedDesc?: string;
  subjectTime?: number;
  descTime?: number;
  /** Suspension/termination state — present when WhatsApp has actioned the group. */
  suspended?: boolean;
  terminated?: boolean;
}

export interface WhatsappMedia {
  file: Blob;
  filename: string;
  mimetype: string;
  caption: string;
}

/**
 * WAWeb chat runtime shape (verified 2026-05-26 against DM and group).
 *
 * Field rules derived from the live store dump:
 * - `id`, `t`, `unreadCount`, `archive`, `isReadOnly`, `pinned`, `muteExpiration`,
 *   `formattedTitle`, `isGroup`, `markedUnread` are ALWAYS present on a loaded chat.
 * - `groupMetadata`, `name`, `groupType`, `isParentGroup`, `hasCapi` ONLY appear
 *   when `isGroup === true`. Narrow on `isGroup` before reading them.
 * - `contact` appears on DMs (1:1 chats) and resolves to the peer's contact model;
 *   absent on groups.
 * - `msgs` is a Mobx collection — iterate via `.msgs._models`, NOT directly.
 *
 * `unreadCount` carries WAWeb's `-1` sentinel for "manually marked unread"
 * (distinct from N actual unread messages). The public API splits this into
 * `unread_count >= 0` + `marked_unread: boolean` at the mapper boundary.
 * Use the `markedUnread` accessor when available; older WAWeb builds only expose
 * the sentinel.
 *
 * The codebase reads more fields than this interface enumerates — add as
 * callsites are tightened. Better to under-declare than to lie with `any`.
 */
export interface Chat {
  id: WhatsappId;
  t: number;
  unreadCount: number;
  markedUnread?: boolean;
  isReadOnly: boolean;
  isLocked: boolean;
  archive: boolean;
  notSpam: boolean;
  pin?: number;
  muteExpiration: number;
  isMuted?: boolean;
  isAutoMuted: boolean;
  hasUnreadMention: boolean;
  unreadMentionCount: number;
  formattedTitle: string;
  isGroup: boolean;
  hasOpened: boolean;
  hasChatBeenOpened: boolean;
  pendingInitialLoading: boolean;
  ephemeralDuration?: number;
  ephemeralSettingTimestamp?: number;
  /** Group-only — present iff `isGroup === true`. Narrow before reading. */
  groupMetadata?: GroupMetaData;
  /** Group-only — chat display name (groups have `name`, DMs have `contact.name`). */
  name?: string;
  /** Group-only — 'default' | 'parent' | 'subgroup'. */
  groupType?: string;
  /** Group-only flags. */
  isParentGroup?: boolean;
  hasCapi?: boolean;
  /** DM-only — the peer's contact model. Absent on groups. */
  contact?: WhatsappContact;
  /** Message collection (Mobx). Read via `.msgs._models`, not direct iteration. */
  msgs?: WhatsappCollection<WhatsappChatMessage>;
  /** Last message accessor (read-only on the WAWeb chat model). */
  lastMessage?: WhatsappChatMessage;
  lastReceivedKey?: WhatsappMessageId;
  /**
   * Group-action methods bound to the WAWeb chat model. Present only on
   * group chats; tsc narrows them to `undefined` on DMs once `isGroup`
   * is checked. Return values are WAWeb-internal — `unknown`, narrow at
   * the callsite.
   */
  setSubject?: (subject: string) => Promise<unknown>;
  /**
   * wwebjs `GroupChat.addParticipants(participantIds, options?)`. The
   * implementation accepts either string ids or WhatsappId objects (it
   * canonicalises via `createWid` internally) — keep the union so callsites
   * that pass raw chat-ids don't need to construct a full wid first.
   */
  addParticipants?: (
    participants: (WhatsappId | string)[],
    options?: {
      autoSendInviteV4?: boolean;
      comment?: string;
      sleep?: [number, number];
    },
  ) => Promise<unknown>;
  /** wwebjs `GroupChat.removeParticipants(participantIds)`. Accepts strings or wids. */
  removeParticipants?: (participants: (WhatsappId | string)[]) => Promise<unknown>;
  labels?: unknown[];
  presence?: unknown;
  mute?: unknown;
  draftMessage?: unknown;
  // ----------------------------------------------------------------------
  // Below are extension-attached fields (NOT WAWeb-native). Kept because
  // legacy code piggybacks on the chat model rather than carrying these
  // in a sidecar. Long-term these should move out of the WAWeb shape.
  // ----------------------------------------------------------------------
  checked?: boolean;
  displayName?: string;
  composeContents?: ComposeContents;
  composeQuotedMsg?: WhatsappChatMessage;
  composeQuotedMsgRemoteJid?: unknown;
  quotedMsgAdminGroupJid?: unknown;
  sendQueue?: unknown;
  addQueue?: unknown;
  attachMediaContents?: WhatsappCollection<WhatsappMedia>;
  urlText?: string;
  canSend?: boolean;
  isMyContact?: boolean;
  isWAContact?: boolean;
  setComposeContents?: (content: ComposeContents) => void;
  setUrlText?: (urlText: string) => void;
  /** `null` clears the compose-pane quote. */
  setQuotedMsg?: (quotedMsg: WhatsappChatMessage | null) => void;
  setQuotedMsgAdminGroupJid?: (quotedMsgAdminGroupJid: unknown | null) => void;
  setQuotedMsgRemoteJid?: (quotedMsgRemoteJid: unknown | null) => void;
}

export interface ChatList {
  onContactClick: (event: Event, chat: Chat) => void;
  getActiveChat: () => Chat;
}

export interface SideBarComponent {
  _chatlist: ChatList;
  getFilteredChats: (filter?: string) => Chat[];
}

export interface ProfilePicRequest {
  eurl: string;
  tag: string;
  token: string;
}

export interface WhatsAppProfilePicService {
  profilePicFind: (chat: Chat) => Promise<ProfilePicRequest>;
}

export interface ProfilePicThumbEvent {
  id: WhatsappId;
  profilePicThumb: Picture;
}

export interface Picture {
  imgFull?: string;
  img?: string;
  id?: WhatsappId;
  profilePicUrl?: string;
}

export interface ProfilePicState {
  profilePicMap: Record<string, Picture>;
}

export interface GetProfilePicResponse {
  waId: string;
  profilePic: string;
}

/** #650 — the parent `album` message: only the expected counts, children attach to its key. */
export interface AlbumParentOptions {
  chat: unknown;
  expectedImageCount: number;
  expectedVideoCount: number;
  /** Wire messageSecret of the parent (a part token, never the message's own). */
  messageSecret?: Uint8Array;
}

/** The association a child media message carries to its album parent. */
export interface AlbumChildAssociation {
  associationType: unknown;
  parentMsgKey: unknown;
  viewMode: unknown;
}

export interface BTWebJS {
  /** #650 — sends the parent album message; resolves to what the children associate with. */
  sendAlbumParent: (options: AlbumParentOptions) => Promise<{ parentMsgKey: unknown; association: AlbumChildAssociation }>;
  sendMessage: (chat: any, content: any, options?: any) => Promise<any>;
  sendPoll: (options: any) => Promise<any>;
  sendPollCreation: (options: any) => Promise<any>;
  processPollOptions: (options: any) => Promise<any>;
  preparePollData: (options: any) => Promise<any>;
  createPollCreationMsgData: (options: any) => Promise<any>;
}

declare global {
    interface Window {
        getStore: () => WAPIStore;
        getAuthStore: () => AuthStore;
        // WWebJSBt is declared (strictly) in
        // extension/wa-communication-layer/core/interfaces/wa-window.d.ts.
        // Do NOT add a permissive shape here — interface merging would union
        // with the strict shape and re-permit `WWebJSBt()` (B16-class bug).
        getBTWebJS: () => BTWebJS;
        require: (moduleName: string) => any;
        safeRequire: (moduleName: string) => any;
        lastMessageTime?: Moment;
    }
}

interface MediaOptions {
    type: string;
    caption?: string;
    isViewOnce?: boolean;
    quotedMsg?: any;
    mentionedJidList?: string[];
    groupMentions?: { groupSubject: string; groupJid: any; }[];
    addEvenWhilePreparing?: boolean;
    placeholderProps?: {
        mimetype: string;
        width?: number;
        height?: number;
        body?: string;
        pageCount?: number;
        filename?: string;
        isGif?: boolean;
        gifAttribution?: any;
    };
}